import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarCheck, MapPin, MessagesSquare, ShieldCheck } from "lucide-react";
import { BTN_GHOST_DARK, BTN_PRIMARY_LG, BOOK_CALL_URL } from "@/lib/site";
import { useTypewriter } from "@/lib/useTypewriter";
import { track } from "@/lib/track";

const TYPED = [
    "AI workflow automations",
    "custom web applications",
    "conversion-focused websites",
    "booking & enquiry systems",
    "white-label agency builds",
];

const TRUST = [
    { icon: MapPin, label: "India-based · working across Asia" },
    { icon: ShieldCheck, label: "Confidential white-label delivery" },
    { icon: MessagesSquare, label: "Direct comms, agreed handover" },
];

const fade = {
    hidden: { opacity: 0, y: 24, filter: "blur(8px)" },
    show: { opacity: 1, y: 0, filter: "blur(0px)" },
};

export default function Hero() {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const typed = useTypewriter(TYPED);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const artY = useTransform(scrollYProgress, [0, 1], [0, -90]);
    const copyY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const fadeOut = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);
    const externalCall = BOOK_CALL_URL.startsWith("http");

    return (
        <section
            id="top"
            ref={ref}
            className="relative isolate overflow-hidden bg-[#0B0A09] pt-16"
            data-testid="hero-section"
        >
            {/* Animated light field */}
            <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                <div className="aurora aurora-a" />
                <div className="aurora aurora-b" />
                <div className="aurora aurora-c" />
                <div className="absolute inset-0 hero-grid" />
                <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-b from-transparent to-[#0B0A09]" />
            </div>

            <motion.div
                style={reduce ? undefined : { opacity: fadeOut }}
                className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-20 pt-16 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:px-8 lg:pb-28 lg:pt-24"
            >
                <motion.div
                    style={reduce ? undefined : { y: copyY }}
                    initial="hidden"
                    animate="show"
                    transition={{ staggerChildren: 0.12 }}
                >
                    <motion.div variants={fade} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
                        <span
                            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.05] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white/75 backdrop-blur-sm"
                            data-testid="hero-eyebrow"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#CF4242] opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#CF4242]" />
                            </span>
                            StackDino · AI Development Studio
                        </span>
                    </motion.div>

                    <motion.h1
                        variants={fade}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-7 font-heading text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[4.1rem]"
                    >
                        Build Smarter.
                        <br />
                        Automate Faster.
                        <br />
                        Grow Without the{" "}
                        <span className="text-gradient-ember font-serif italic">Complexity.</span>
                    </motion.h1>

                    <motion.p
                        variants={fade}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-7 flex min-h-[2rem] flex-wrap items-baseline gap-x-2 font-heading text-lg text-white/55 sm:text-xl"
                        data-testid="hero-typewriter"
                    >
                        <span>We build</span>
                        <span className="font-semibold text-white">
                            {typed}
                            <span className="caret" aria-hidden="true" />
                        </span>
                    </motion.p>

                    <motion.p
                        variants={fade}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-5 max-w-xl text-base leading-relaxed text-white/60 md:text-lg"
                    >
                        An independent studio for businesses and digital agencies across Asia.
                        We turn ideas and repetitive work into premium digital products that
                        quietly run your operations.
                    </motion.p>

                    <motion.div
                        variants={fade}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-discuss-project-btn"
                            onClick={() => track("cta_click", { cta: "hero_discuss_project" })}
                            className={BTN_PRIMARY_LG}
                        >
                            Discuss Your Project <ArrowRight size={16} />
                        </a>
                        <a
                            href={BOOK_CALL_URL}
                            target={externalCall ? "_blank" : undefined}
                            rel={externalCall ? "noopener noreferrer" : undefined}
                            data-testid="hero-book-call-btn"
                            onClick={() => track("cta_click", { cta: "hero_book_call" })}
                            className={BTN_GHOST_DARK}
                        >
                            <CalendarCheck size={16} /> Book a Free Call
                        </a>
                    </motion.div>

                    <motion.ul
                        variants={fade}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-7"
                        data-testid="hero-trust-strip"
                    >
                        {TRUST.map(({ icon: Icon, label }) => (
                            <li
                                key={label}
                                className="flex items-center gap-2 text-xs font-semibold text-white/55"
                            >
                                <Icon size={14} className="text-[#E8764A]" aria-hidden="true" />
                                {label}
                            </li>
                        ))}
                    </motion.ul>
                </motion.div>

                {/* Floating product composition */}
                <motion.div
                    style={reduce ? undefined : { y: artY }}
                    initial={reduce ? false : { opacity: 0, y: 40, rotateX: 8 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    className="relative [perspective:1400px]"
                >
                    <div className="relative pb-16 lg:pl-6">
                        <div className="glass-frame float-slow">
                            <BrowserBar domain="gonomikai.com" />
                            <img
                                src="/assets/gonomikai-live.png"
                                alt="GoNomikai live website — a hospitality booking experience designed and built by StackDino"
                                width={1440}
                                height={900}
                                className="h-auto w-full"
                                fetchPriority="high"
                                data-testid="hero-screenshot-gonomikai"
                            />
                        </div>

                        <div className="glass-frame float-slower absolute -bottom-6 left-0 hidden w-[54%] sm:block">
                            <BrowserBar domain="nomiconnekt.in" small />
                            <img
                                src="/assets/nomiconnekt-live.png"
                                alt="NomiConnekt live product — property management software for hotel and hostel operations"
                                width={1440}
                                height={900}
                                className="h-auto w-full"
                                data-testid="hero-screenshot-nomiconnekt"
                            />
                        </div>

                        <motion.div
                            initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 1 }}
                            className="absolute -top-5 right-0 hidden items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.07] px-4 py-3 shadow-[0_16px_40px_-10px_rgb(0,0,0,0.8)] backdrop-blur-xl md:flex"
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#CF4242]/20 ring-1 ring-[#CF4242]/40">
                                <span className="h-2 w-2 animate-pulse rounded-full bg-[#E8764A]" />
                            </span>
                            <div>
                                <p className="text-sm font-semibold text-white">Live & shipped</p>
                                <p className="text-xs text-white/55">Real products, real traffic</p>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </motion.div>

            <motion.a
                href="#work"
                aria-label="Scroll to our work"
                data-testid="hero-scroll-cue"
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                className="group absolute inset-x-0 bottom-6 mx-auto hidden w-fit flex-col items-center gap-2 lg:flex"
            >
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/35 transition-colors duration-300 group-hover:text-white/70">
                    Scroll
                </span>
                <span className="flex h-9 w-5 items-start justify-center rounded-full border border-white/20 p-1">
                    <span className="h-1.5 w-1 animate-bounce rounded-full bg-[#E8764A]" />
                </span>
            </motion.a>
        </section>
    );
}

const BrowserBar = ({ domain, small = false }) => (
    <div
        className={`flex items-center gap-1.5 border-b border-white/10 bg-white/[0.06] ${
            small ? "px-3 py-2" : "px-4 py-2.5"
        }`}
        aria-hidden="true"
    >
        <span className="h-2 w-2 rounded-full bg-[#CF4242]/70" />
        <span className="h-2 w-2 rounded-full bg-[#E8764A]/70" />
        <span className="h-2 w-2 rounded-full bg-white/25" />
        <span
            className={`ml-2 rounded-full bg-black/30 font-medium text-white/50 ${
                small ? "px-2.5 py-0.5 text-[10px]" : "px-3 py-1 text-[11px]"
            }`}
        >
            {domain}
        </span>
    </div>
);
